// src/components/opportunity/OpportunityTodosTab.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import { isOpportunityTodosEditable } from "@/lib/opportunity/oppTodoItemTypes";
import type { OppTodoItem } from "@/lib/opportunity/oppTodoItemTypes";
import {
  deleteOppTodoItem,
  listOppTodoItems,
  reorderOppTodoItems,
  toggleOppTodoItem,
  upsertOppTodoItem,
} from "@/lib/opportunity/oppTodoItemClient";
import { OpportunityTodoItemRow } from "@/components/opportunity/OpportunityTodoItemRow";

export type OpportunityTodosTabProps = {
  opportunity: Opportunity;
  reloadKey?: number;
};

export function OpportunityTodosTab({ opportunity, reloadKey = 0 }: OpportunityTodosTabProps) {
  const editable = isOpportunityTodosEditable(opportunity.status);
  const [items, setItems] = useState<OppTodoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listOppTodoItems(opportunity.id);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setItems([]);
      return;
    }
    setItems(r.data.items);
  }, [opportunity.id]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const displayItems = useMemo(
    () => [...items].sort((a, b) => a.sort_order - b.sort_order),
    [items],
  );

  const openCount = displayItems.filter((item) => item.completed_at == null).length;

  async function addItem() {
    const nextOrder =
      displayItems.length > 0 ? Math.max(...displayItems.map((s) => s.sort_order)) + 1 : 0;
    const r = await upsertOppTodoItem({
      opportunity_id: opportunity.id,
      label: "New step",
      sort_order: nextOrder,
      due_date: null,
    });
    if (r.ok) await load();
    else setError(r.error);
  }

  async function moveItem(id: string, direction: "up" | "down") {
    const idx = displayItems.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= displayItems.length) return;

    const reordered = [...displayItems];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swapIdx];
    reordered[swapIdx] = tmp;

    const r = await reorderOppTodoItems(
      opportunity.id,
      reordered.map((s) => s.id),
    );
    if (r.ok) await load();
    else setError(r.error);
  }

  return (
    <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Todo list</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
            Post-sale checklist for this deal. Template steps copy in automatically the first time
            status becomes <strong>In process</strong>.
          </p>
        </div>
        {editable ? (
          <button
            type="button"
            onClick={() => void addItem()}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
          >
            + Add step
          </button>
        ) : null}
      </div>

      {openCount > 0 ? (
        <p className="text-xs text-amber-700 dark:text-amber-200">
          {openCount} open {openCount === 1 ? "item" : "items"}
        </p>
      ) : displayItems.length > 0 ? (
        <p className="text-xs text-emerald-700 dark:text-emerald-300">All steps complete</p>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading todos…</p>
      ) : displayItems.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          {editable
            ? "No steps yet. Set status to In process to copy org templates, or add steps manually."
            : "No post-sale steps on this opportunity yet."}
        </p>
      ) : (
        <div className="space-y-2">
          {displayItems.map((item, idx) => (
            <OpportunityTodoItemRow
              key={item.id}
              item={item}
              editable={editable}
              canMoveUp={editable && idx > 0}
              canMoveDown={editable && idx < displayItems.length - 1}
              onToggle={async (id, completed) => {
                const r = await toggleOppTodoItem(id, completed);
                if (r.ok) await load();
                return r.ok ? { ok: true } : { ok: false, error: r.error };
              }}
              onSave={async (payload) => {
                const r = await upsertOppTodoItem(payload);
                if (r.ok) await load();
                return r.ok ? { ok: true } : { ok: false, error: r.error };
              }}
              onDelete={async (id) => {
                const r = await deleteOppTodoItem(id);
                if (r.ok) await load();
                return r.ok ? { ok: true } : { ok: false, error: r.error };
              }}
              onMove={(dir) => void moveItem(item.id, dir)}
            />
          ))}
        </div>
      )}

      {!editable && displayItems.length > 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Locked — todos can only be edited while status is Ordered or In process.
        </p>
      ) : null}
    </section>
  );
}
